// Generic offline-first repository.
//
// Pattern Steve specified on day one, in plain language:
//   • Mutations land in IndexedDB first (instant, offline-safe).
//   • Then we try the server. If the server is reachable → cache the
//     authoritative server response. If not → enqueue for later sync.
//   • Reads fetch from the server (source of truth) and fall back to the
//     IndexedDB cache when offline.
//   • Conflict resolution is server-wins: if the server returns a record
//     newer than a queued local mutation, the queued mutation is dropped
//     and a 'conflict' event fires so the UI can flag it.
//
// The repo is generic on the record type — Submissions today, Contacts next.

import type { TkosDb } from "./db";
import type { SyncQueue } from "./sync-queue";

export type RepoEvent = "change" | "conflict";

export interface RepoConflictPayload<T> {
  recordId: string;
  serverVersion: T;
  localPending: unknown;
}

type Listener = (...args: unknown[]) => void;

export interface RepoOptions<T extends { id: string }> {
  /** IndexedDB object store name, e.g. `submissions`. */
  store: string;
  /** Collection endpoint, e.g. `/portal/submissions`. */
  endpoint: string;
  /** Typed fetch helper bound to the calling portal's api client. */
  api: (path: string, init?: RequestInit) => Promise<Response>;
  db: TkosDb;
  queue: SyncQueue;
  /** Field used to compare server vs local clocks. Default `updatedAt`. */
  versionField?: keyof T;
  /**
   * Server response shape may wrap the record (e.g. `{ submission: {...} }`).
   * The repo just needs the unwrapped record(s); the page passes adapters
   * that match its existing backend.
   */
  parseList: (json: unknown) => T[];
  parseOne: (json: unknown) => T | undefined;
}

export interface Repo<T extends { id: string }> {
  /** PG-first; falls back to IndexedDB on offline / 5xx. */
  list(): Promise<T[]>;
  create(record: T): Promise<T>;
  update(id: string, patch: Partial<T>): Promise<T>;
  remove(id: string): Promise<void>;
  on(event: RepoEvent, fn: Listener): () => void;
  /** Snapshot of the IndexedDB cache. */
  cached(): Promise<T[]>;
}

function isNetworkOrServerError(err: unknown): boolean {
  // The api function rejects on any non-2xx; we need to distinguish 4xx (bad
  // data, don't queue) from 5xx / network (queue + retry). The caller wraps
  // raw fetch and surfaces the Response, so here we treat any thrown error
  // as a network-level failure.
  return err instanceof Error;
}

export function createRepo<T extends { id: string }>(
  opts: RepoOptions<T>,
): Repo<T> {
  const versionField: keyof T = opts.versionField ?? ("updatedAt" as keyof T);
  const listeners = new Map<RepoEvent, Set<Listener>>();

  function emit(event: RepoEvent, ...args: unknown[]): void {
    const set = listeners.get(event);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(...args);
      } catch (err) {
        console.error(`[repo:${opts.store}] listener threw`, err);
      }
    }
  }

  /**
   * Raw fetch wrapper. We don't reuse the typed `api()` helper because the
   * sync-queue API contracts on Response, not parsed JSON — and we need to
   * inspect the status code to decide between queue and bubble.
   */
  async function rawFetch(
    path: string,
    init?: RequestInit,
  ): Promise<Response> {
    // Caller passes a function that already prefixes API_BASE; we forward.
    // We treat the api fn as returning Response — see opts.api signature.
    return opts.api(path, init);
  }

  async function triggerDrain(): Promise<void> {
    try {
      await opts.queue.drain(rawFetch);
    } catch (err) {
      console.error(`[repo:${opts.store}] drain failed`, err);
    }
  }

  async function list(): Promise<T[]> {
    try {
      const res = await rawFetch(opts.endpoint, { method: "GET" });
      if (!res.ok) {
        if (res.status >= 500 || res.status === 503) {
          return opts.db.getAll<T>(opts.store);
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const json: unknown = await res.json();
      const serverRecords = opts.parseList(json);

      // Conflict pass: compare any pending queued mutation against the server
      // record's version. Server wins; we drop the queued op and emit.
      const pending = await opts.queue.list();
      const pendingByRecord = new Map<string, typeof pending>();
      for (const op of pending) {
        if (op.store !== opts.store) continue;
        const arr = pendingByRecord.get(op.recordId) ?? [];
        arr.push(op);
        pendingByRecord.set(op.recordId, arr);
      }
      for (const rec of serverRecords) {
        const ops = pendingByRecord.get(rec.id);
        if (!ops || ops.length === 0) continue;
        const serverVersion = rec[versionField];
        for (const op of ops) {
          const localBody =
            op.body && typeof op.body === "object"
              ? (op.body as Record<string, unknown>)
              : undefined;
          const localVersion =
            localBody && versionField in localBody
              ? localBody[versionField as string]
              : undefined;
          // Server wins if server version is newer (or local has no version).
          const isConflict =
            serverVersion !== undefined &&
            (localVersion === undefined ||
              (typeof serverVersion === "number" &&
                typeof localVersion === "number" &&
                serverVersion > localVersion) ||
              (typeof serverVersion === "string" &&
                typeof localVersion === "string" &&
                serverVersion > localVersion));
          if (isConflict) {
            // Drop the queued op — server's record is authoritative.
            // We re-list rather than mutate the queue directly to keep
            // queue internals encapsulated.
            const allOps = await opts.queue.list();
            for (const stale of allOps) {
              if (stale.id === op.id) {
                // No public delete API on the queue; piggy-back on internals
                // by performing a successful "drain step" — emit conflict
                // first so the UI sees it, then drop.
                emit("conflict", {
                  recordId: rec.id,
                  serverVersion: rec,
                  localPending: op,
                } satisfies RepoConflictPayload<T>);
                await opts.db.remove("_sync_queue", stale.id);
                break;
              }
            }
          }
        }
      }

      // Replace IndexedDB cache with server truth.
      await opts.db.clear(opts.store);
      await opts.db.putMany(opts.store, serverRecords);
      emit("change");
      // A successful server read is a good moment to try queued writes too.
      void triggerDrain();
      return serverRecords;
    } catch (err) {
      if (isNetworkOrServerError(err)) {
        // Offline / network failure → serve from cache.
        return opts.db.getAll<T>(opts.store);
      }
      throw err;
    }
  }

  async function cached(): Promise<T[]> {
    return opts.db.getAll<T>(opts.store);
  }

  async function create(record: T): Promise<T> {
    // 1. Optimistic local write.
    await opts.db.put(opts.store, record);
    emit("change");

    // 2. Try the server.
    try {
      const res = await rawFetch(opts.endpoint, {
        method: "POST",
        body: JSON.stringify(record),
      });
      if (res.ok) {
        const json: unknown = await res.json();
        const server = opts.parseOne(json);
        if (server) {
          // The server may have rewritten id/timestamps; reconcile cache.
          if (server.id !== record.id) {
            await opts.db.remove(opts.store, record.id);
          }
          await opts.db.put(opts.store, server);
          emit("change");
          void triggerDrain();
          return server;
        }
        void triggerDrain();
        return record;
      }
      if (res.status >= 400 && res.status < 500) {
        // Validation failure — DO NOT queue invalid data.
        // Roll back the local optimistic write so the cache mirrors the
        // server's view: this record does not exist.
        await opts.db.remove(opts.store, record.id);
        emit("change");
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      // 5xx → queue
      await opts.queue.enqueue({
        store: opts.store,
        recordId: record.id,
        action: "create",
        body: record,
        endpoint: opts.endpoint,
      });
      void triggerDrain();
      return record;
    } catch (err) {
      // Network failure → queue (we already have the optimistic write).
      // Only queue if it wasn't a 4xx we already rolled back above.
      if (err instanceof Error && /^HTTP 4/.test(err.message)) throw err;
      await opts.queue.enqueue({
        store: opts.store,
        recordId: record.id,
        action: "create",
        body: record,
        endpoint: opts.endpoint,
      });
      void triggerDrain();
      return record;
    }
  }

  async function update(id: string, patch: Partial<T>): Promise<T> {
    const current = await opts.db.get<T>(opts.store, id);
    const merged = { ...(current ?? ({ id } as T)), ...patch } as T;
    await opts.db.put(opts.store, merged);
    emit("change");

    const url = `${opts.endpoint}/${encodeURIComponent(id)}`;
    try {
      const res = await rawFetch(url, {
        method: "PUT",
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const json: unknown = await res.json();
        const server = opts.parseOne(json);
        if (server) {
          await opts.db.put(opts.store, server);
          emit("change");
        }
        void triggerDrain();
        return server ?? merged;
      }
      if (res.status >= 400 && res.status < 500) {
        // Bad patch — roll back to whatever we had before and bubble.
        if (current) {
          await opts.db.put(opts.store, current);
        } else {
          await opts.db.remove(opts.store, id);
        }
        emit("change");
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      await opts.queue.enqueue({
        store: opts.store,
        recordId: id,
        action: "update",
        body: patch,
        endpoint: url,
      });
      void triggerDrain();
      return merged;
    } catch (err) {
      if (err instanceof Error && /^HTTP 4/.test(err.message)) throw err;
      await opts.queue.enqueue({
        store: opts.store,
        recordId: id,
        action: "update",
        body: patch,
        endpoint: url,
      });
      void triggerDrain();
      return merged;
    }
  }

  async function remove(id: string): Promise<void> {
    await opts.db.remove(opts.store, id);
    emit("change");

    const url = `${opts.endpoint}/${encodeURIComponent(id)}`;
    try {
      const res = await rawFetch(url, { method: "DELETE" });
      if (res.ok) {
        void triggerDrain();
        return;
      }
      if (res.status >= 400 && res.status < 500) {
        // 404 on delete is fine — the record was already gone server-side.
        if (res.status === 404) {
          void triggerDrain();
          return;
        }
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      await opts.queue.enqueue({
        store: opts.store,
        recordId: id,
        action: "delete",
        endpoint: url,
      });
      void triggerDrain();
    } catch (err) {
      if (err instanceof Error && /^HTTP 4/.test(err.message)) throw err;
      await opts.queue.enqueue({
        store: opts.store,
        recordId: id,
        action: "delete",
        endpoint: url,
      });
      void triggerDrain();
    }
  }

  function on(event: RepoEvent, fn: Listener): () => void {
    let set = listeners.get(event);
    if (!set) {
      set = new Set();
      listeners.set(event, set);
    }
    set.add(fn);
    return (): void => {
      set!.delete(fn);
    };
  }

  return { list, create, update, remove, on, cached };
}
